import { createContext, useCallback, useContext, useEffect, useRef } from 'react';

import { Check } from 'lucide-react';

import { Button } from './Button';
import { cn } from './utils';


/**
 * Radio group primitive, extracted from the two consumers that hand-rolled it:
 * `pages/Settings/InviteModal.jsx` (`RoleOption`, the dot indicator) and
 * `pages/Sales/fulfillment/RatePicker.jsx` (`RateRow`, whose comment read "no
 * radio primitive exists"). Matches the .pen kit RADIO atom (aAsBy dark /
 * Yo1eq light): a circular indicator, `primary` ring + inner dot when selected,
 * `hairline-strong` hairline when not.
 *
 * Like `filterPrimitives.jsx`, the base + selected/unselected classes are the
 * shared invariant and genuine per-screen differences (alignment, padding,
 * unselected fill) ride on `className`.
 *
 * The group adds what neither call site had: `role="radiogroup"`, arrow-key
 * navigation and a roving tabindex, so the group is one tab stop and the arrows
 * move (and change) the selection, per the WAI-ARIA radio pattern.
 *
 *   <RadioGroup value={role} onValueChange={setRole} aria-label="Role">
 *     <Radio value="lister">Lister</Radio>
 *   </RadioGroup>
 */
const RadioGroupContext = createContext({ value: undefined, onValueChange: undefined });

const ARROW_STEP = {
  ArrowDown: 1,
  ArrowRight: 1,
  ArrowUp: -1,
  ArrowLeft: -1,
};


function enabledRadios(root) {
  return Array.from(root?.querySelectorAll('[role="radio"]:not(:disabled)') || []);
}


function RadioGroup({ value, onValueChange, className, children, ...restProps }) {
  const rootRef = useRef(null);

  // Roving tabindex: only the checked option is tabbable (the first one when
  // nothing is checked yet), so the group is a single tab stop. Driven off the
  // DOM rather than the children so it survives wrapped/mapped options.
  useEffect(() => {
    const items = enabledRadios(rootRef.current);
    const hasChecked = items.some((item) => item.getAttribute('aria-checked') === 'true');
    for (const [index, item] of items.entries()) {
      const tabbable = item.getAttribute('aria-checked') === 'true' || (!hasChecked && index === 0);
      item.tabIndex = tabbable ? 0 : -1;
    }
  });

  const onKeyDown = useCallback((event) => {
    const step = ARROW_STEP[event.key];
    if (!step) {
      return;
    }
    const items = enabledRadios(rootRef.current);
    if (items.length === 0) {
      return;
    }
    event.preventDefault();
    const current = items.indexOf(document.activeElement);
    const next = items[(((current < 0 ? 0 : current + step) % items.length) + items.length) % items.length];
    // Selection follows focus, per the WAI-ARIA radio-group pattern.
    next?.focus();
    next?.click();
  }, []);

  return (
    <div
      ref={rootRef}
      className={cn('flex flex-col gap-2.5', className)}
      role="radiogroup"
      onKeyDown={onKeyDown}
      {...restProps}
    >
      <RadioGroupContext.Provider value={{ value, onValueChange }}>
        {children}
      </RadioGroupContext.Provider>
    </div>
  );
}

RadioGroup.displayName = 'RadioGroup';


/** The 18px circular indicator. `dot` is the .pen default (2px primary ring +
 * inner dot); `check` is the filled-tick treatment the rate rows ship. */
function RadioIndicator({ checked, indicator, className }) {
  if (indicator === 'check') {
    return (
      <span
        aria-hidden="true"
        className={cn(
          'grid size-[18px] shrink-0 place-items-center rounded-full border',
          checked ? 'border-primary bg-primary text-on-primary' : 'border-hairline-strong',
          className,
        )}
      >
        {checked ? <Check className="size-3" /> : null}
      </span>
    );
  }

  return (
    <span
      aria-hidden="true"
      className={cn(
        'grid size-[18px] shrink-0 place-items-center rounded-full border-2',
        checked ? 'border-primary' : 'border-hairline-strong',
        className,
      )}
    >
      {checked ? <span className="size-2.5 rounded-full bg-primary" /> : null}
    </span>
  );
}

RadioIndicator.displayName = 'RadioIndicator';


function Radio(props) {
  const {
    value, indicator = 'dot', indicatorClassName, className, children, disabled,
  } = props;
  const group = useContext(RadioGroupContext);
  const checked = group.value === value;

  return (
    <Button
      type="button"
      variant="ghost"
      disabled={disabled}
      aria-checked={checked}
      className={cn(
        'h-auto items-center justify-start gap-3 rounded-lg border px-3.5 py-3 text-left',
        checked
          ? 'border-primary bg-primary-soft hover:bg-primary-soft hover:text-ink'
          : 'border-hairline bg-surface-1 text-ink hover:bg-surface-2 hover:text-ink',
        className,
      )}
      role="radio"
      onClick={() => group.onValueChange?.(value)}
    >
      <RadioIndicator checked={checked} className={indicatorClassName} indicator={indicator} />
      {children}
    </Button>
  );
}

Radio.displayName = 'Radio';

export { Radio, RadioGroup };
export default RadioGroup;
